import React from "react";
import { ArrowUp, Mail, ExternalLink } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const divisions = [
    { name: "Travel Club Foundation", href: "https://prestigioustravelclub.org/" },
    { name: "Prestigious Travel Club", href: "https://prestigioustravelclub.net/" },
    { name: "Prestigious Marketing", href: "https://pmllc.org/" },
    { name: "Prestigious Apps", href: null },
    { name: "Easy Quick Fund", href: "https://easyquickfund.com/" },
    { name: "CreditForward360", href: "https://creditforward360.com" },
    { name: "Prestigious Hub", href: "https://hub.prestigiouscompanies.org" },
    { name: "Prestigious TV", href: "https://Prestigious-TV.com" },
    { name: "Ghost™ Assistant", href: "#ghost" },
  ];

  const quickLinks = [
    { name: "About Us", href: "#about" },
    { name: "Our Brands", href: "#divisions" },
    { name: "Impact", href: "#impact" },
    { name: "Contact", href: "#contact" },
  ];

  const charities = [
    { name: "St. Jude", href: "https://www.stjude.org/" },
    { name: "Feeding America", href: "https://www.feedingamerica.org/" },
    { name: "Wounded Warrior Project", href: "https://www.woundedwarriorproject.org/" },
    { name: "DAV", href: "https://www.dav.org/" },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (href) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="bg-[#0A192F] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-8 lg:mb-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#0A192F] font-bold text-xl font-['Outfit']">P</span>
              </div>
              <span className="text-lg font-semibold font-['Outfit']">
                Prestigious
              </span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              One Prestigious Mission — Many Paths to Empowerment. A family of companies united by purpose.
            </p>
          </div>

          {/* Our Brands */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-5 text-[#D4AF37]">
              Our Companies
            </h4>
            <ul className="space-y-3">
              {divisions.map((item) => (
                <li key={item.name}>
                  {item.href ? (
                    item.href.startsWith("#") ? (
                      <button
                        onClick={() => scrollToSection(item.href)}
                        className="footer-link text-sm flex items-center gap-1 group"
                        data-testid={`footer-link-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {item.name}
                      </button>
                    ) : (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-link text-sm flex items-center gap-1 group"
                        data-testid={`footer-link-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {item.name}
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    )
                  ) : (
                    <span className="text-white/40 text-sm">{item.name}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-5 text-[#D4AF37]">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.name}>
                  <button
                    onClick={() => scrollToSection(item.href)}
                    className="footer-link text-sm"
                    data-testid={`footer-link-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Charity Partners */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-5 text-[#D4AF37]">
              Charity Partners
            </h4>
            <ul className="space-y-3">
              {charities.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-link text-sm flex items-center gap-1 group"
                    data-testid={`footer-charity-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.name}
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-5 text-[#D4AF37]">
              Contact
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:info@prestigiouscompanies.org"
                  className="footer-link text-sm flex items-center gap-2"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4" />
                  Email Us
                </a>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("#contact")}
                  className="footer-link text-sm"
                  data-testid="footer-contact-form"
                >
                  Contact Form
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            {currentYear} Prestigious Companies. All rights reserved.
          </p>
          
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
            data-testid="scroll-to-top"
          >
            Back to Top
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
